import React, { createContext, useContext, useState, useCallback, useRef } from "react";
import { FaCheckCircle, FaExclamationTriangle, FaInfoCircle, FaTimesCircle } from "react-icons/fa";

const ModalContext = createContext();

const ICONS = {
  success: { icon: FaCheckCircle, color: "text-green-500", bg: "bg-green-500/10" },
  warning: { icon: FaExclamationTriangle, color: "text-yellow-500", bg: "bg-yellow-500/10" },
  error: { icon: FaTimesCircle, color: "text-red-500", bg: "bg-red-500/10" },
  info: { icon: FaInfoCircle, color: "text-brand", bg: "bg-brand/10" },
  confirm: { icon: FaExclamationTriangle, color: "text-yellow-500", bg: "bg-yellow-500/10" },
  prompt: { icon: FaInfoCircle, color: "text-brand", bg: "bg-brand/10" },
};

export function ModalProvider({ children }) {
  const [modal, setModal] = useState(null);
  const resolveRef = useRef(null);
  const inputRef = useRef(null);

  const close = useCallback((value) => {
    if (resolveRef.current) resolveRef.current(value);
    resolveRef.current = null;
    setModal(null);
  }, []);

  const open = useCallback((config) => {
    return new Promise((resolve) => {
      resolveRef.current = resolve;
      setModal(config);
    });
  }, []);

  // alert(message, variant?) — resolves when user clicks OK
  const alert = useCallback((message, variant = "info") => {
    return open({ type: "alert", message, variant });
  }, [open]);

  // confirm(message, opts?) — resolves true/false
  const confirm = useCallback((message, opts = {}) => {
    return open({
      type: "confirm",
      message,
      variant: opts.variant || "confirm",
      confirmText: opts.confirmText || "Confirm",
      cancelText: opts.cancelText || "Cancel",
      danger: opts.danger || false,
    });
  }, [open]);

  // prompt(message, opts?) — resolves string or null
  const prompt = useCallback((message, opts = {}) => {
    return open({
      type: "prompt",
      message,
      variant: "prompt",
      placeholder: opts.placeholder || "",
      defaultValue: opts.defaultValue || "",
      inputType: opts.inputType || "text",
      autoComplete: opts.autoComplete || "off",
      description: opts.description || "",
      confirmText: opts.confirmText || "OK",
      cancelText: opts.cancelText || "Cancel",
    });
  }, [open]);

  const iconCfg = modal ? ICONS[modal.variant] || ICONS.info : null;
  const Icon = iconCfg?.icon;

  return (
    <ModalContext.Provider value={{ alert, confirm, prompt }}>
      {children}

      {modal && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4" onClick={() => close(modal.type === "confirm" ? false : modal.type === "prompt" ? null : undefined)}>
          {/* Backdrop */}
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />

          {/* Modal */}
          <div
            className="relative bg-surface-card rounded-xl shadow-2xl border border-surface-border w-full max-w-md animate-modal-in"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6">
              {/* Icon + Message */}
              <div className="flex gap-4">
                {Icon && (
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${iconCfg.bg}`}>
                    <Icon size={18} className={iconCfg.color} />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-ink-primary leading-relaxed">{modal.message}</p>
                  {modal.description && (
                    <p className="text-xs text-ink-muted leading-relaxed mt-1.5">{modal.description}</p>
                  )}

                  {/* Prompt input */}
                  {modal.type === "prompt" && (
                    <input
                      ref={inputRef}
                      autoFocus
                      type={modal.inputType}
                      autoComplete={modal.autoComplete}
                      className="input mt-3 w-full"
                      placeholder={modal.placeholder}
                      defaultValue={modal.defaultValue}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") close(e.target.value);
                        if (e.key === "Escape") close(null);
                      }}
                    />
                  )}
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-2 px-6 pb-5">
              {modal.type === "alert" && (
                <button className="btn-primary btn-sm" autoFocus onClick={() => close(undefined)}>
                  OK
                </button>
              )}

              {modal.type === "confirm" && (
                <>
                  <button className="btn-secondary btn-sm" onClick={() => close(false)}>
                    {modal.cancelText}
                  </button>
                  <button
                    className={`${modal.danger ? "btn-danger" : "btn-primary"} btn-sm`}
                    autoFocus
                    onClick={() => close(true)}
                  >
                    {modal.confirmText}
                  </button>
                </>
              )}

              {modal.type === "prompt" && (
                <>
                  <button className="btn-secondary btn-sm" onClick={() => close(null)}>
                    {modal.cancelText}
                  </button>
                  <button
                    className="btn-primary btn-sm"
                    onClick={() => close(inputRef.current?.value ?? "")}
                  >
                    {modal.confirmText}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </ModalContext.Provider>
  );
}

export const useModal = () => useContext(ModalContext);
